import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'APP/Hooks/redux'
import { SupportedFileWithMetadata } from 'APP/Converse/FilePreview/types'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import {
  buildUploadPayload,
  canQueueFileWeb,
  createFileWithMetadata,
  isPendingFile,
  updateFileStatus,
  WebQueueFileInput,
} from 'APP/Lib/FileUploadHelpers'
import { type ChannelUploadSnapshot, FileUploadStatusEnum } from 'APP/Store/History/types'

type FileWithRaw = SupportedFileWithMetadata & {
  rawFile?: File
}

export const useUploadManager = (
  channelId: string | null,
  maxFiles: number,
  channelUpload?: ChannelUploadSnapshot
) => {
  const dispatch = useDispatch()
  const [files, setFiles] = useState<FileWithRaw[]>([])
  const filesRef = useRef(files)
  const objectUrlsRef = useRef<Set<string>>(new Set())
  const [pendingUpload, setPendingUpload] = useState<{
    file: WebQueueFileInput
    fileType: string
  } | null>(null)

  const hasPendingUpload = useMemo(() => {
    return files.some(isPendingFile)
  }, [files])

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    if (!pendingUpload || !channelId) return

    const payload = buildUploadPayload(pendingUpload.file, pendingUpload.fileType)
    dispatch(PatientHistoryActions.uploadFileRequest(channelId, payload))
    setPendingUpload(null)
  }, [pendingUpload, channelId, dispatch])

  useEffect(() => {
    if (!channelUpload) return

    const { status, fileId, fileInfo, error } = channelUpload

    if (!status && !fileId && !error) return

    setFiles((prevFiles) => {
      if (!prevFiles.length) return prevFiles

      const updatedFiles = prevFiles.map((file) =>
        updateFileStatus(file, status, fileId, fileInfo, error)
      )

      return updatedFiles.filter((file) => file.uploadStatus !== FileUploadStatusEnum.FAILED)
    })
  }, [
    channelUpload?.status,
    channelUpload?.fileId,
    channelUpload?.fileInfo?.id,
    channelUpload?.error,
  ])

  const queueFile = useCallback(
    (incomingFile: WebQueueFileInput) => {
      const currentFiles = filesRef.current

      if (!canQueueFileWeb(currentFiles, maxFiles, incomingFile.file)) {
        return false
      }

      const objectUrl = URL.createObjectURL(incomingFile.file)
      objectUrlsRef.current.add(objectUrl)

      const fileInput = {
        ...incomingFile,
        uri: objectUrl,
      }
      const fileWithMetadata = createFileWithMetadata(fileInput)

      const fileWithRaw: FileWithRaw = {
        ...fileWithMetadata,
        rawFile: incomingFile.file,
      }

      const nextFiles = [...currentFiles, fileWithRaw]
      filesRef.current = nextFiles
      setFiles(nextFiles)

      setPendingUpload({ file: incomingFile, fileType: fileWithMetadata.fileType })

      return true
    },
    [maxFiles]
  )

  const removeFile = useCallback(
    (file: SupportedFileWithMetadata, index: number) => {
      setFiles((prevFiles) => {
        const fileToRemove = prevFiles[index]

        if (fileToRemove?.uri && objectUrlsRef.current.has(fileToRemove.uri)) {
          URL.revokeObjectURL(fileToRemove.uri)
          objectUrlsRef.current.delete(fileToRemove.uri)
        }

        return prevFiles.filter((_, fileIndex) => fileIndex !== index)
      })

      if (!channelId) return

      let isUploadedToServer = false

      if (file.fileId) {
        const hasBeenPermanentlyUploaded =
          !file.fileId.startsWith('temp-') && file.uploadStatus === 'uploaded'
        if (hasBeenPermanentlyUploaded) {
          isUploadedToServer = true
          dispatch(PatientHistoryActions.removeUploadedFileRequest(channelId, file.fileId))
        }
      }

      if (!isUploadedToServer) {
        dispatch(PatientHistoryActions.fileUploadClear(channelId))
      }
    },
    [channelId, dispatch]
  )

  const replaceFile = useCallback(
    (incomingFile: WebQueueFileInput, index: number) => {
      const currentFiles = filesRef.current

      if (index < 0 || index >= currentFiles.length) {
        return false
      }

      const fileToReplace = currentFiles[index]

      if (!fileToReplace) {
        return false
      }

      // Revoke old object URL
      if (fileToReplace.uri && objectUrlsRef.current.has(fileToReplace.uri)) {
        URL.revokeObjectURL(fileToReplace.uri)
        objectUrlsRef.current.delete(fileToReplace.uri)
      }

      const objectUrl = URL.createObjectURL(incomingFile.file)
      objectUrlsRef.current.add(objectUrl)

      const fileInput = {
        ...incomingFile,
        uri: objectUrl,
      }
      const fileWithMetadata = createFileWithMetadata(fileInput)

      const fileWithRaw: FileWithRaw = {
        ...fileWithMetadata,
        rawFile: incomingFile.file,
      }

      const nextFiles = [
        ...currentFiles.slice(0, index),
        fileWithRaw,
        ...currentFiles.slice(index + 1),
      ]

      filesRef.current = nextFiles
      setFiles(nextFiles)

      setPendingUpload({ file: incomingFile, fileType: fileWithMetadata.fileType })

      if (channelId && fileToReplace.fileId) {
        dispatch(PatientHistoryActions.removeUploadedFileRequest(channelId, fileToReplace.fileId))
      }

      return true
    },
    [channelId, dispatch]
  )

  const resetUploads = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current.clear()
    setFiles([])

    if (channelId) {
      dispatch(PatientHistoryActions.fileUploadClear(channelId))
    }
  }, [channelId, dispatch])

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current.clear()
    }
  }, [])

  return useMemo(
    () => ({
      files,
      hasPendingUpload,
      queueFile,
      removeFile,
      replaceFile,
      resetUploads,
    }),
    [files, hasPendingUpload, queueFile, removeFile, replaceFile, resetUploads]
  )
}
